import {
  AnalyticsImplementationBlueprint,
  configApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import posthog from 'posthog-js';

/**
 * Adoção medida no próprio portal.
 *
 * A pergunta que isto responde não é "quantas visitas o portal teve", e sim
 * "quem, de qual squad, chegou a usar alguma coisa daqui". Time sem autoridade
 * formal só tem adoção como moeda; sem este dado, a conversa com a liderança
 * volta a ser sobre opinião.
 *
 * O identificador é o squad, não a pessoa: o que interessa é qual time entrou
 * no caminho pavimentado, e agregar por time evita transformar o portal num
 * painel de vigilância de indivíduo.
 */
/** O formato de evento que o app entrega a qualquer implementação de analytics. */
type Evento = {
  action: string;
  subject: string;
  value?: number;
  attributes?: Record<string, string | number | boolean>;
  context: Record<string, unknown>;
};

class AnalyticsPostHog {
  private constructor(private readonly ligado: boolean) {}

  static criar(opts: { chave?: string; host?: string }) {
    const ligado = Boolean(opts.chave);
    if (ligado) {
      posthog.init(opts.chave!, {
        api_host: opts.host ?? 'https://us.i.posthog.com',
        // Sem gravação de sessão e sem captura automática: só os eventos que
        // o portal declara. Menos dado, mais confiança.
        autocapture: false,
        disable_session_recording: true,
        capture_pageview: false,
        person_profiles: 'identified_only',
      });
    }
    return new AnalyticsPostHog(ligado);
  }

  captureEvent(evento: Evento) {
    if (!this.ligado) return;
    posthog.capture(evento.action, {
      assunto: evento.subject,
      valor: evento.value,
      caminho: evento.context.routeRef,
      plugin: evento.context.pluginId,
      extensao: evento.context.extension,
      ...evento.attributes,
    });
  }

  identificar(squad: string) {
    if (this.ligado) posthog.identify(squad, { squad });
  }
}

export const analyticsDoPortal = AnalyticsImplementationBlueprint.make({
  name: 'posthog',
  params: define =>
    define({
      deps: { configApi: configApiRef, identityApi: identityApiRef },
      factory: ({ configApi, identityApi }) => {
        const api = AnalyticsPostHog.criar({
          chave: configApi.getOptionalString('lemon.posthog.key'),
          host: configApi.getOptionalString('lemon.posthog.host'),
        });
        // O perfil do Backstage já sabe a que grupo a pessoa pertence.
        identityApi
          .getBackstageIdentity()
          .then(identidade => {
            const grupo = identidade.ownershipEntityRefs.find(ref =>
              ref.startsWith('group:'),
            );
            if (grupo) api.identificar(grupo.split('/').pop()!);
          })
          .catch(() => undefined);
        return api;
      },
    }),
});
